"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useFormik } from "formik";
import { isBD } from "../../../../utils/businessType.lib.js";
import { DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION, createIMask, validateDeliveryLocation } from "../../../../utils/validations/deliveryLocations.js";
import { useRef, useState } from "react";
import { Notification, TextField, Button } from "@nxt-costco-com/forge-components";
import { SpaceXs, SpaceMd, SpaceSm } from "@nxt-costco-com/forge-design-tokens";
import { Analytics } from "../../../Analytics/index.js";
import { usFullAddressFormValidationSchema, toBoolean } from "./utils.js";
import { countryCodeMap } from "./constants.js";
import { encodeAddressInfo } from "./lib.js";
const DeliveryAddressForm = (props) => {
  const addressLine1InputRef = useRef(null);
  const zipInputRef = useRef(null);
  const abortControllerHandler = useRef(null);
  const { agent, data, onSubmit, tmpZipCode, locale } = props;
  const {
    aptSuiteUnitEtcInputField,
    errorNoMatchForAddress,
    zipcodeInputLabel,
    buttonLabel,
    optionalLabelText,
    serviceDownErrorMessage,
    validationSchemaType = "en-us",
    streetAddressInputLabel,
    addressLine1LabelText,
    addressLine2LabelText,
    errorMsgInputValidAddrText,
    errorMsgInputValidZipCodeText
  } = data || {};
  const zipValidationConfiguration = DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION[validationSchemaType];
  const validationSchema = usFullAddressFormValidationSchema;
  const zipMaxCharacterLimit = zipValidationConfiguration == null ? void 0 : zipValidationConfiguration.maxCharacterLimit;
  const zipInputMaskConfiguration = zipValidationConfiguration == null ? void 0 : zipValidationConfiguration.inputMaskConfiguration;
  const zipInputMask = createIMask(zipInputMaskConfiguration);
  const initialValues = {
    addressLine1: "",
    addressLine2: "",
    zipCode: tmpZipCode || ""
  };
  const [tmpErrorConfig, setTmpErrorConfig] = useState();
  const isBDPage = isBD();
  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      abortControllerHandler.current = new AbortController();
      const encodedAddressInfo = encodeAddressInfo({
        line1: values.addressLine1,
        line2: values.addressLine2,
        zip: values.zipCode,
        country: countryCodeMap[locale]
      });
      const response = await agent.getZipByAddress(encodedAddressInfo);
      const { jsonValidatedAddress } = response || {};
      const parsedValidatedAddress = jsonValidatedAddress && (JSON.parse(jsonValidatedAddress) || {});
      const { returnedAddress, returnedZip } = parsedValidatedAddress;
      const { validbdzip } = returnedAddress || {};
      const isValidBDZip = toBoolean(validbdzip);
      let deliveryLocationResponse;
      if (returnedZip && isValidBDZip) {
        deliveryLocationResponse = await validateDeliveryLocation(
          agent,
          validationSchema,
          { ...values, zipCode: returnedZip },
          {
            isBDPage: true,
            onlyValidate: true
          },
          locale
        );
      }
      if (!isValidBDZip) {
        setTmpErrorConfig({
          type: "noMatchForAddress",
          severity: "critical"
        });
        return;
      }
      if (!deliveryLocationResponse) {
        setTmpErrorConfig({
          type: "server",
          severity: "alert"
        });
        return;
      }
      const {
        warehouse,
        country,
        city,
        state,
        errorConfig,
        deliveryMethodsConfig
      } = deliveryLocationResponse;
      if (returnedZip && onSubmit) {
        onSubmit({
          ...values,
          zipCode: returnedZip,
          city: city || "",
          state: state || "",
          warehouse: warehouse || "",
          country: country || "",
          errorConfig,
          // don't include this unless it's returned by the API; should only be used on BD
          ...!!deliveryMethodsConfig && { deliveryMethodsConfig }
        });
      }
    },
    validate: async (values) => {
      var _a, _b;
      const { errors, errorConfig } = await validateDeliveryLocation(
        agent,
        validationSchema,
        values,
        { isBDPage, onlyValidate: true },
        locale
      );
      setTmpErrorConfig(errorConfig);
      if ((tmpErrorConfig == null ? void 0 : tmpErrorConfig.type) !== "server" && errors) {
        if (errors.zipCode) {
          (_a = zipInputRef == null ? void 0 : zipInputRef.current) == null ? void 0 : _a.focus();
        }
        if (errors.addressLine1) {
          (_b = addressLine1InputRef == null ? void 0 : addressLine1InputRef.current) == null ? void 0 : _b.focus();
        }
        return errors;
      }
    },
    validateOnBlur: false,
    validateOnChange: false
  });
  if (!data) return null;
  if (!zipValidationConfiguration) {
    console.error(
      `Missing valid validationSchemaType: Options are ${Object.keys(DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION).join(" | ")}`
    );
    return null;
  }
  const handleAddressChange = (e) => {
    return formik.handleChange(e);
  };
  const handleZipcodeChange = (e) => {
    const formattedValue = zipInputMask.resolve(e.target.value);
    e.target.value = formattedValue;
    return formik.handleChange(e);
  };
  const handleSubmit = (event) => {
    formik.validateForm().then(() => {
      event.preventDefault();
      formik.handleSubmit();
    });
  };
  return /* @__PURE__ */ jsx(
    Analytics,
    {
      analyticData: {
        component: {
          trackedData: [
            formik.values.addressLine1,
            formik.values.addressLine2,
            formik.values.zipCode
          ]
        }
      },
      children: /* @__PURE__ */ jsxs("form", { "data-testid": "deliverylocationform", onSubmit: handleSubmit, children: [
        (tmpErrorConfig == null ? void 0 : tmpErrorConfig.type) === "server" && /* @__PURE__ */ jsx(
          Notification,
          {
            message: serviceDownErrorMessage,
            severity: "alert",
            sx: {
              marginTop: SpaceSm,
              marginBottom: SpaceMd,
              padding: SpaceXs
            }
          }
        ),
        (tmpErrorConfig == null ? void 0 : tmpErrorConfig.type) === "noMatchForAddress" && errorNoMatchForAddress && /* @__PURE__ */ jsx(
          Notification,
          {
            message: errorNoMatchForAddress,
            severity: "critical",
            sx: {
              marginTop: SpaceSm,
              marginBottom: SpaceMd,
              padding: SpaceXs
            }
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            errorText: formik.errors.addressLine1 ? errorMsgInputValidAddrText : void 0,
            sx: {
              marginBottom: 4
            },
            id: "addressLine1",
            inputLabelId: addressLine1LabelText,
            inputRef: addressLine1InputRef,
            isError: !!formik.errors.addressLine1,
            isRequired: true,
            label: addressLine1LabelText,
            helpText: streetAddressInputLabel,
            helpTextId: streetAddressInputLabel,
            name: "addressLine1",
            onBlur: formik.handleBlur,
            onChange: handleAddressChange,
            size: "small",
            type: "text",
            uniqueId: "deliverylocationform--address-line-1",
            value: formik.values.addressLine1
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            id: "addressLine2",
            inputLabelId: addressLine2LabelText,
            isError: !!formik.errors.addressLine2,
            isRequired: false,
            labelOptionalText: optionalLabelText,
            label: addressLine2LabelText,
            helpText: aptSuiteUnitEtcInputField,
            helpTextId: aptSuiteUnitEtcInputField,
            name: "addressLine2",
            onBlur: formik.handleBlur,
            onChange: handleAddressChange,
            size: "small",
            type: "text",
            uniqueId: "deliverylocationform--address-line-2",
            value: formik.values.addressLine2
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            errorText: formik.errors.zipCode ? errorMsgInputValidZipCodeText : void 0,
            sx: {
              marginBottom: 4,
              marginTop: 4
            },
            id: "zipCode",
            inputLabelId: zipcodeInputLabel,
            inputRef: zipInputRef,
            isError: !!formik.errors.zipCode,
            isRequired: true,
            label: zipcodeInputLabel,
            maxCharacterLimit: zipMaxCharacterLimit,
            name: "zipCode",
            onBlur: formik.handleBlur,
            onChange: handleZipcodeChange,
            size: "small",
            type: "text",
            uniqueId: "deliverylocationform--zipcode",
            value: formik.values.zipCode
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            sx: {
              width: "100%"
            },
            "data-testid": "deliverylocationform--submit",
            onClick: formik.handleSubmit,
            type: "submit",
            children: buttonLabel
          }
        )
      ] })
    }
  );
};
export {
  DeliveryAddressForm
};
